import { ref, computed } from 'vue';

/**
 * Composable per gestire stato loading con stack per operazioni concorrenti
 * Include debounce anti-flickering per stabilizzare l'UI
 */
export function useLoadingState() {
	// Stack delle operazioni loading attive
	const loadingStack = ref<string[]>([]);

	// Stato interno del loading UI (separato dal logic state)
	const isLoadingVisible = ref(false);

	// Timeout references per gestire i delay
	let showTimeout: NodeJS.Timeout | null = null;
	let hideTimeout: NodeJS.Timeout | null = null;

	// Configurazione debounce
	const MIN_SHOW_DELAY = 300; // Minimo 150ms prima di mostrare loading (evita flash)
	const HIDE_DELAY = 200; // 100ms debounce prima di nascondere

	// Computed che ritorna true se ci sono operazioni in corso (logic state)
	const hasActiveOperations = computed(() => loadingStack.value.length > 0);

	/**
	 * Aggiorna la visibilità del loading con debounce intelligente
	 */
	const updateLoadingVisibility = (): void => {
		const shouldShow = hasActiveOperations.value;

		// Clear existing timeouts
		if (showTimeout) {
			clearTimeout(showTimeout);
			showTimeout = null;
		}
		if (hideTimeout) {
			clearTimeout(hideTimeout);
			hideTimeout = null;
		}

		if (shouldShow && !isLoadingVisible.value) {
			// Show loading with minimum delay (anti-flash per operazioni rapide)
			showTimeout = setTimeout(() => {
				if (hasActiveOperations.value) {
					// Double check
					isLoadingVisible.value = true;
				}
			}, MIN_SHOW_DELAY);
		} else if (!shouldShow && isLoadingVisible.value) {
			// Hide loading with debounce (stabilizza UI)
			hideTimeout = setTimeout(() => {
				if (!hasActiveOperations.value) {
					// Double check
					isLoadingVisible.value = false;
				}
			}, HIDE_DELAY);
		}
	};

	// Computed che espone lo stato stabile (quello che vede l'UI)
	const isLoading = computed(() => isLoadingVisible.value);

	/**
	 * Aggiunge un'operazione loading allo stack
	 */
	const pushLoading = (operation: string = 'default'): void => {
		loadingStack.value.push(operation);
		updateLoadingVisibility();
	};

	/**
	 * Rimuove un'operazione loading dallo stack
	 */
	const popLoading = (operation: string = 'default'): void => {
		const index = loadingStack.value.indexOf(operation);
		if (index >= 0) {
			loadingStack.value.splice(index, 1);
			updateLoadingVisibility();
		}
	};

	/**
	 * Utility wrapper per gestire automaticamente loading con try/finally
	 * Garantisce che popLoading sia sempre chiamato anche in caso di errore
	 */
	async function withLoading<T>(fn: () => Promise<T>, operation = 'default'): Promise<T> {
		pushLoading(operation);
		try {
			return await fn();
		} finally {
			popLoading(operation);
		}
	}

	/**
	 * Reset completo dello stack (per cleanup o emergency)
	 */
	const clearAllLoading = (): void => {
		// Clear timeouts
		if (showTimeout) {
			clearTimeout(showTimeout);
			showTimeout = null;
		}
		if (hideTimeout) {
			clearTimeout(hideTimeout);
			hideTimeout = null;
		}

		loadingStack.value = [];
		isLoadingVisible.value = false;
	};

	/**
	 * Computed per debugging - lista delle operazioni attive
	 */
	const activeOperations = computed(() => [...loadingStack.value]);

	return {
		// State
		isLoading,
		activeOperations,

		// Methods
		pushLoading,
		popLoading,
		withLoading,
		clearAllLoading,
	};
}
